import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Document, Page } from 'react-pdf';
import { Button } from "@/components/ui/button";
import { RotateCw, Trash2, Undo2, Redo2 } from 'lucide-react';
import { PDFDocument, degrees } from 'pdf-lib';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

interface PDFViewerProps {
  file: File | null;
}

export interface PDFViewerRef {
  getEditedPdfBlob: () => Promise<Blob | null>;
  rotation: number;
}

interface SortablePageProps {
  id: number;
  pageNumber: number;
  rotation: number;
  onRotate: (event: React.MouseEvent) => void;
  onDelete: (event: React.MouseEvent) => void;
}

const SortablePage = ({ id, pageNumber, rotation, onRotate, onDelete }: SortablePageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={`group relative border rounded-lg p-1 hover:shadow-lg transition-all cursor-grab active:cursor-grabbing
        ${isDragging ? 'shadow-xl ring-2 ring-cyan-400 bg-gray-800/50' : 'hover:bg-gray-800/30'}`}
    >
      <div className="relative">
        <div className="select-none">
          <Page
            pageNumber={pageNumber}
            width={200}
            rotate={rotation}
          />
        </div>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="bg-gray-800/80 hover:bg-gray-700/80 h-8 w-8 p-1.5"
            onClick={onDelete}
            title="ページを削除"
          >
            <Trash2 className="h-full w-full text-red-400" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="bg-gray-800/80 hover:bg-gray-700/80 h-8 w-8 p-1.5"
            onClick={onRotate}
            title="90度回転"
          >
            <RotateCw className="h-full w-full text-cyan-400" />
          </Button>
        </div>
      </div>
      <div className="text-center mt-1 text-sm text-gray-600 select-none">
        ページ {pageNumber}
      </div>
    </div>
  );
};

const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>((props, ref) => {
  const { file } = props;
  const [numPages, setNumPages] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pageRotations, setPageRotations] = useState<{ [key: number]: number }>({});
  const [deletedPages, setDeletedPages] = useState<Set<number>>(new Set());
  const [pageOrder, setPageOrder] = useState<number[]>([]);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);

  interface HistoryState {
    pageRotations: { [key: number]: number };
    deletedPages: Set<number>;
    pageOrder: number[];
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 3,
      },
    })
  );

  useImperativeHandle(ref, () => ({
    getEditedPdfBlob: async () => {
      if (!file) return null;
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pages = pdfDoc.getPages();
        
        const newPdfDoc = await PDFDocument.create();
        // ページの順序に従ってPDFを再構築
        for (const pageNum of pageOrder) {
          if (!deletedPages.has(pageNum)) {
            const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageNum - 1]);
            newPdfDoc.addPage(copiedPage);
            if (pageRotations[pageNum]) {
              const existingRotation = copiedPage.getRotation().angle;
              copiedPage.setRotation(degrees(existingRotation + pageRotations[pageNum]));
            }
          }
        }
        
        const modifiedPdfBytes = await newPdfDoc.save();
        return new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      } catch (error) {
        console.error('Error processing PDF:', error);
        setError('PDFの処理中にエラーが発生しました');
        return null;
      }
    },
    rotation: pageRotations[selectedPage || 1] || 0
  }), [file, pageRotations, selectedPage, deletedPages, pageOrder]);

  useEffect(() => {
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('PDFファイル以外はアップロードできません');
        setPdfUrl(null);
        return;
      }

      try {
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        setError(null);
        setPageRotations({});
        setDeletedPages(new Set());
        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (err) {
        console.error('Error creating object URL:', err);
        setError('PDFファイルの処理中にエラーが発生しました');
      }
    } else {
      setPdfUrl(null);
      setError(null);
    }
  }, [file]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setError(null);
    const initialPageOrder = Array.from({ length: numPages }, (_, i) => i + 1);
    setPageOrder(initialPageOrder);
    
    // 初期状態を履歴に保存
    const initialState: HistoryState = {
      pageRotations: {},
      deletedPages: new Set(),
      pageOrder: initialPageOrder,
    };
    setHistory([initialState]);
    setCurrentHistoryIndex(0);
  }

  const handleRotate = (pageNumber: number, event: React.MouseEvent): void => {
    event.stopPropagation();
    const newRotations = { ...pageRotations };
    const currentRotation = newRotations[pageNumber] || 0;
    newRotations[pageNumber] = (currentRotation + 90) % 360;
    setPageRotations(newRotations);

    // 回転操作を履歴に保存
    const currentState: HistoryState = {
      pageRotations: newRotations,
      deletedPages: new Set(deletedPages),
      pageOrder: [...pageOrder],
    };
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    setHistory([...newHistory, currentState]);
    setCurrentHistoryIndex(currentHistoryIndex + 1);
  };

  const handleDelete = (pageNumber: number, event: React.MouseEvent): void => {
    event.stopPropagation();
    const newDeletedPages = new Set(deletedPages);
    newDeletedPages.add(pageNumber);
    setDeletedPages(newDeletedPages);

    // 削除操作を履歴に保存
    const currentState: HistoryState = {
      pageRotations: { ...pageRotations },
      deletedPages: newDeletedPages,
      pageOrder: [...pageOrder],
    };
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    setHistory([...newHistory, currentState]);
    setCurrentHistoryIndex(currentHistoryIndex + 1);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedId(null);

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = pageOrder.indexOf(active.id as number);
      const newIndex = pageOrder.indexOf(over.id as number);

      const newOrder = arrayMove(pageOrder, oldIndex, newIndex);
      
      const newState: HistoryState = {
        pageRotations: { ...pageRotations },
        deletedPages: new Set(deletedPages),
        pageOrder: newOrder,
      };
      
      setHistory([...history.slice(0, currentHistoryIndex + 1), newState]);
      setCurrentHistoryIndex(currentHistoryIndex + 1);
      setPageOrder(newOrder);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id as number);
  };

  const undo = () => {
    if (currentHistoryIndex > 0) {
      const previousState = history[currentHistoryIndex - 1];
      setPageRotations({ ...previousState.pageRotations });
      setDeletedPages(new Set(previousState.deletedPages));
      setPageOrder([...previousState.pageOrder]);
      setCurrentHistoryIndex(currentHistoryIndex - 1);
    }
  };

  const redo = () => {
    if (currentHistoryIndex < history.length - 1) {
      const nextState = history[currentHistoryIndex + 1];
      setPageRotations({ ...nextState.pageRotations });
      setDeletedPages(new Set(nextState.deletedPages));
      setPageOrder([...nextState.pageOrder]);
      setCurrentHistoryIndex(currentHistoryIndex + 1);
    }
  };

  function onDocumentLoadError(error: Error): void {
    console.error('PDF loading error:', error);
    setError(`PDFファイルの読み込みに失敗しました: ${error.message}`);
  }

  if (!file) {
    return null;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const activePages = pageOrder.filter(pageNum => !deletedPages.has(pageNum));

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-end gap-2">
        <Button
          size="icon"
          variant="outline"
          onClick={undo}
          disabled={currentHistoryIndex <= 0}
          title="元に戻す"
          className="bg-gray-800/80 hover:bg-gray-700/80"
        >
          <Undo2 className="h-4 w-4 text-cyan-400" />
        </Button>
        <Button
          size="icon"
          variant="outline"
          onClick={redo}
          disabled={currentHistoryIndex >= history.length - 1}
          title="やり直し"
          className="bg-gray-800/80 hover:bg-gray-700/80"
        >
          <Redo2 className="h-4 w-4 text-cyan-400" />
        </Button>
      </div>
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
      >
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={activePages}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 gap-3 p-2">
              {activePages.map((pageNumber) => (
                <SortablePage
                  key={pageNumber}
                  id={pageNumber}
                  pageNumber={pageNumber}
                  rotation={pageRotations[pageNumber] || 0}
                  onRotate={(e) => handleRotate(pageNumber, e)}
                  onDelete={(e) => handleDelete(pageNumber, e)}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {draggedId ? (
              <div className="opacity-80 shadow-2xl ring-4 ring-cyan-400 rounded-lg bg-gray-800/90">
                <Page
                  pageNumber={draggedId}
                  width={200}
                  rotate={pageRotations[draggedId] || 0}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Document>
    </div>
  );
});

PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;